// Click and bulk-selection handlers for TaskListBase row interaction.
import { useCallback } from 'react';

/**
 * @param {{
 *   tasks: Task[],
 *   selectedIndex: number,
 *   newTaskPosition: unknown,
 *   cancel: () => void,
 *   focusContainer: () => void,
 *   selectSingleTask: (id: string) => void,
 *   toggleTaskInSelection: (id: string) => void,
 *   selectRangeTasks: (ids: string[]) => void,
 *   setIsBulkSelectMode: (value: boolean) => void,
 * }} params
 */
export function useTaskListClickHandlers({
  tasks,
  selectedIndex,
  newTaskPosition,
  cancel,
  focusContainer,
  selectSingleTask,
  toggleTaskInSelection,
  selectRangeTasks,
  setIsBulkSelectMode,
}) {
  const handleTaskClick = useCallback(
    (taskId, event) => {
      event.stopPropagation();
      focusContainer();
      if (newTaskPosition) cancel();
      if (event.shiftKey && selectedIndex > -1) {
        const clickedIndex = tasks.findIndex((t) => t.id === taskId);
        if (clickedIndex > -1) {
          const [start, end] =
            selectedIndex < clickedIndex
              ? [selectedIndex, clickedIndex]
              : [clickedIndex, selectedIndex];
          setIsBulkSelectMode(true);
          selectRangeTasks(tasks.slice(start, end + 1).map((t) => t.id));
          return;
        }
      }
      if (event.ctrlKey || event.metaKey) {
        setIsBulkSelectMode(true);
        toggleTaskInSelection(taskId);
      } else {
        setIsBulkSelectMode(false);
        selectSingleTask(taskId);
      }
    },
    [
      focusContainer,
      toggleTaskInSelection,
      selectSingleTask,
      selectRangeTasks,
      selectedIndex,
      tasks,
      newTaskPosition,
      cancel,
      setIsBulkSelectMode,
    ],
  );

  const handleLongPressSelect = useCallback(
    (taskId) => {
      setIsBulkSelectMode(true);
      toggleTaskInSelection(taskId);
    },
    [toggleTaskInSelection, setIsBulkSelectMode],
  );

  return { handleTaskClick, handleLongPressSelect };
}
